import { useSelector, useDispatch } from 'react-redux';
import {
  useMemo, useCallback, useState, useRef, useEffect,
} from 'react';
import localStore from 'store';
import {
  FIELDS_CONFIG,
  MAX_CONDITIONS_PER_FIELD,
} from '../constants/eligibilityEngine';
import { normalizeActionOnNull } from '../utilities/eligibilityEngine';
import {
  fetchProducts as fetchProductsAction,
  setSelectedProduct,
  setRules,
  setHasUnsavedChanges,
  updateRule,
  fetchRules as fetchRulesAction,
  setNextEffectiveFrom,
  publishRules,
  upsertRuleNote,
  fetchExternalHistory,
  fetchExternalHistoryByRuleKey,
  addRule,
  addRuleSorted,
  removeRule,
} from '../data/eligibilityEngine/actions';
import { getProductType } from '../constants/productFamily';
import client from '../apollo';
import allRuleDefinitionsQuery from '../graphql/allRuleDefinitionsQuery';

const selectEligibilityEngine = state => state.data.eligibilityEngine;

export const useEligibilityProducts = () => {
  const {
    products, productsLoading, productsError, selectedProduct,
  } = useSelector(selectEligibilityEngine);
  return {
    products,
    productsLoading,
    productsError,
    selectedProduct,
  };
};

export const useEligibilityRules = () => {
  const {
    rules, isRulesLoading, rulesError, nextEffectiveFrom,
  } = useSelector(
    selectEligibilityEngine
  );
  return {
    rules,
    isRulesLoading,
    rulesError,
    nextEffectiveFrom,
  };
};

export const useEligibilityFields = () => {
  const { rules, fieldOverrides } = useSelector(selectEligibilityEngine);

  const fields = useMemo(() => {
    let merged = FIELDS_CONFIG;

    if (rules.length) {
      const rulesMap = {};
      rules.forEach(rule => {
        rulesMap[rule.name] = rule;
      });

      merged = FIELDS_CONFIG.map(fieldConfig => {
        const rule = rulesMap[fieldConfig.id];
        if (!rule) return fieldConfig;

        let parsedValue;
        try {
          parsedValue = JSON.parse(rule.value);
        } catch {
          parsedValue = rule.value;
        }

        if (
          parsedValue !== null
          && typeof parsedValue === 'object'
          && !Array.isArray(parsedValue)
        ) {
          return {
            ...fieldConfig,
            ...parsedValue,
            id: rule.id,
            actionOnNull: normalizeActionOnNull(rule.actionOnNull),
            ...(fieldConfig.staticText !== undefined
              ? { staticText: fieldConfig.staticText }
              : {}),
          };
        }

        return {
          ...fieldConfig,
          value: parsedValue,
          id: rule.id,
          actionOnNull: normalizeActionOnNull(rule.actionOnNull),
        };
      });
    }

    return merged.map(field => {
      const override = fieldOverrides[field.id];
      if (!override) return field;
      return { ...field, ...override };
    });
  }, [rules, fieldOverrides]);

  return fields;
};

export const useHasUnsavedChanges = () => {
  const { hasUnsavedChanges } = useSelector(selectEligibilityEngine);
  return hasUnsavedChanges;
};

/**
 * Returns the pending activation ISO date string for the currently selected
 * product, or null if there is no pending activation.
 */
export const usePendingActivation = () => {
  const { nextEffectiveFrom } = useSelector(selectEligibilityEngine);
  return nextEffectiveFrom || null;
};

export const useEligibilityTheme = () => {
  const { products, selectedProduct } = useEligibilityProducts();

  const selectedProductData = useMemo(
    () => products.find(p => p.categoryCode === selectedProduct),
    [products, selectedProduct]
  );

  const productType = useMemo(
    () => getProductType(selectedProductData?.displayName),
    [selectedProductData]
  );

  const themeClass = productType === 'seller-assurance' ? 'theme-green' : 'theme-blue';

  return {
    productType,
    themeClass,
    selectedProductData,
    selectedProduct,
  };
};

export const useEligibilityActions = () => {
  const dispatch = useDispatch();
  const fields = useEligibilityFields();
  const { selectedProduct, rules } = useSelector(selectEligibilityEngine);

  const handleFetchProducts = useCallback(() => {
    dispatch(fetchProductsAction());
  }, [dispatch]);

  const handleFetchRules = useCallback(
    productId => {
      dispatch(fetchRulesAction(productId));
    },
    [dispatch]
  );

  const handleSetRules = useCallback(
    ruleList => {
      dispatch(setRules({ rules: ruleList, productCode: selectedProduct }));
    },
    [dispatch, selectedProduct]
  );

  const handleProductChange = useCallback(
    productId => {
      dispatch(setSelectedProduct(productId));
      localStore.set('eligibilitySelectedProduct', productId);
    },
    [dispatch]
  );

  const handleValueChange = useCallback(
    (ruleId, key, value) => {
      // If called as onValueChange(ruleId, fullValue) (no third arg),
      // treat `key` as either the full changes object or a JSON string for `value`.
      if (value === undefined && key !== undefined) {
        if (typeof key === 'object') {
          dispatch(updateRule({ ruleId, changes: key }));
          dispatch(setHasUnsavedChanges(true));
          return;
        }
        // If `key` is a string, we assume it's the JSON stringified `value` for the rule.
        if (typeof key === 'string') {
          dispatch(updateRule({ ruleId, changes: { value: key } }));
          dispatch(setHasUnsavedChanges(true));
          return;
        }
      }

      const currentField = fields.find(f => f.instanceId === ruleId);
      if (!currentField) return;

      if (key === 'bucket') {
        const newBuckets = [...(currentField.buckets || [])];
        newBuckets[value.index] = {
          ...newBuckets[value.index],
          value: value.value,
        };
        dispatch(updateRule({ ruleId, changes: { buckets: newBuckets } }));
        dispatch(setHasUnsavedChanges(true));
        return;
      }

      if (key === 'condition') {
        const newConditions = [...(currentField.conditions || [])];
        newConditions[value.index] = {
          ...newConditions[value.index],
          [value.key]: value.value,
        };
        dispatch(
          updateRule({
            ruleId,
            changes: { conditions: newConditions },
          })
        );
        dispatch(setHasUnsavedChanges(true));
        return;
      }

      dispatch(updateRule({ ruleId, changes: { [key]: value } }));
      dispatch(setHasUnsavedChanges(true));
    },
    [dispatch, fields]
  );

  const handleIfNullChange = useCallback(
    (ruleId, value) => {
      dispatch(updateRule({ ruleId, changes: { actionOnNull: value } }));
      dispatch(setHasUnsavedChanges(true));
    },
    [dispatch]
  );

  const handleAddCondition = useCallback(
    ruleId => {
      const currentField = fields.find(f => f.instanceId === ruleId);
      if (!currentField) return;

      const effectiveMax = currentField.maxConditions ?? MAX_CONDITIONS_PER_FIELD;

      let currentCount = 0;
      if (currentField.conditions && Array.isArray(currentField.conditions)) {
        currentCount = currentField.conditions.length;
      } else if (currentField.valueType === 'comparison_value') {
        currentCount = 1;
      }

      if (currentCount >= effectiveMax) return;

      if (currentField.conditions && Array.isArray(currentField.conditions)) {
        dispatch(
          updateRule({
            ruleId,
            changes: {
              conditions: [
                ...currentField.conditions,
                {
                  includeExclude: 'include',
                  comparison: 'gte',
                  field: currentField.label || '',
                  value: '',
                },
              ],
            },
          })
        );
        dispatch(setHasUnsavedChanges(true));
        return;
      }

      if (currentField.valueType === 'comparison_value') {
        dispatch(
          updateRule({
            ruleId,
            changes: {
              valueType: 'condition_builder',
              conditions: [
                {
                  includeExclude: currentField.includeExclude || 'include',
                  comparison: currentField.comparison || 'gte',
                  field: currentField.label || '',
                  value: currentField.comparisonValue || '',
                },
                {
                  includeExclude: 'include',
                  comparison: 'gte',
                  field: currentField.label || '',
                  value: '',
                },
              ],
            },
          })
        );
        dispatch(setHasUnsavedChanges(true));
      }
    },
    [dispatch, fields]
  );

  const handleRevert = useCallback(() => {
    // Reload rules from API to revert all changes
    if (selectedProduct) {
      dispatch(fetchRulesAction(selectedProduct));
    }
  }, [dispatch, selectedProduct]);

  const handlePublish = useCallback(
    ({ effectiveFrom }) => dispatch(publishRules({ effectiveFrom })),
    [dispatch]
  );

  const handleUpsertNote = useCallback(
    ({ productCode, ruleId, note }) => dispatch(upsertRuleNote({ productCode, ruleId, note })),
    [dispatch]
  );
  const handleAddRule = useCallback(
    (rule, allDefinitions) => {
      if (allDefinitions && allDefinitions.length > 0) {
        const sortedRules = [...rules, rule].sort((a, b) => {
          const iA = allDefinitions.findIndex(d => d.id === a.id);
          const iB = allDefinitions.findIndex(d => d.id === b.id);
          return (iA === -1 ? Infinity : iA) - (iB === -1 ? Infinity : iB);
        });
        dispatch(addRuleSorted(sortedRules));
      } else {
        dispatch(addRule(rule));
      }
    },
    [dispatch, rules]
  );

  const handleClearPendingActivation = useCallback(
    () => dispatch(setNextEffectiveFrom(null)),
    [dispatch]
  );

  const handleRemoveRule = useCallback(
    instanceId => {
      dispatch(removeRule(instanceId));
    },
    [dispatch]
  );

  return {
    fetchProducts: handleFetchProducts,
    fetchRules: handleFetchRules,
    setRules: handleSetRules,
    onProductChange: handleProductChange,
    onValueChange: handleValueChange,
    onIfNullChange: handleIfNullChange,
    onAddCondition: handleAddCondition,
    onRevert: handleRevert,
    onPublish: handlePublish,
    upsertNote: handleUpsertNote,
    clearPendingActivation: handleClearPendingActivation,
    onAddRule: handleAddRule,
    onRemoveRule: handleRemoveRule,
  };
};

export const useStatusHistoryFetcher = () => {
  const dispatch = useDispatch();

  const fetchHistory = useCallback(
    async({
      productCode, ruleKey, from, to,
    }) => {
      if (ruleKey) {
        return dispatch(
          fetchExternalHistoryByRuleKey({
            productCode,
            ruleKey,
            from,
            to,
          })
        );
      }
      return dispatch(fetchExternalHistory({ productCode, from, to }));
    },
    [dispatch]
  );

  return { fetchHistory };
};

export const useRuleDefinitions = () => {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const fetchDefinitions = useCallback(async() => {
    setLoading(true);
    setError(null);
    try {
      const result = await client.query({
        query: allRuleDefinitionsQuery,
        fetchPolicy: 'network-only',
      });
      if (mountedRef.current) setDefinitions(result.data.externalAllRules || []);
    } catch (err) {
      if (mountedRef.current) setError('Failed to load rule definitions. Please try again.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  return {
    definitions,
    loading,
    error,
    fetchDefinitions,
  };
};
